import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { Alert, Snackbar } from '@mui/material';

type Notice = Readonly<{
  text: string;
  severity: 'success' | 'warning' | 'error';
  /** Monotonic, so a repeat of identical text is still a NEW notice. */
  id: number;
}>;

type NoticeApi = Readonly<{
  announce: (text: string, severity?: Notice['severity']) => void;
}>;

const NoticeContext = createContext<NoticeApi | null>(null);

const AUTO_HIDE_MS = 5000;

/**
 * One notice at a time, hosted above the tables. A confirmation rendered beside the row it
 * came from shifts every row below it, which moves the control out from under the pointer.
 */
export const NoticeHost = ({ id, children }: { id: string; children: ReactNode }) => {
  const [notice, setNotice] = useState<Notice | null>(null);

  const api = useMemo<NoticeApi>(
    () => ({
      announce: (text, severity = 'success') =>
        setNotice((previous) => ({ text, severity, id: (previous?.id ?? 0) + 1 })),
    }),
    [],
  );

  return (
    <NoticeContext.Provider value={api}>
      {children}
      <Snackbar
        // Keyed on the id rather than the text, because the same text repeats: copy confirmations
        // are deterministic per code. Keyed on text, a second copy of the SAME code re-arms
        // nothing — MUI's auto-hide effect sees no changed dependency, so the notice vanishes on
        // the first click's clock — and the Alert's text node is unchanged, so aria-live announces
        // nothing. Remounting per notice gives both a fresh timer and a fresh announcement.
        key={notice?.id}
        open={notice !== null}
        autoHideDuration={AUTO_HIDE_MS}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          id={id}
          onClose={() => setNotice(null)}
          severity={notice?.severity ?? 'success'}
          sx={{ width: '100%' }}
        >
          {notice?.text ?? ''}
        </Alert>
      </Snackbar>
    </NoticeContext.Provider>
  );
};

/**
 * Outside a host the notice is dropped — but it says so. This is the only channel a surface's
 * write outcomes travel on, so one mounted in the wrong place loses all of them at once.
 */
export const useNotice = (): NoticeApi => {
  const api = useContext(NoticeContext);
  const fallback = useCallback(
    (text: string) => console.error('A notice was dropped — no notice host above this tree', text),
    [],
  );

  return api ?? { announce: fallback };
};
